import React, { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Badge, ErrorState } from '@/components/common'
import { useCopilotChat } from '@/hooks/useCopilotChat'
import { ApiError } from '@/lib/apiClient'
import { friendlyErrorMessage } from '@/hooks/copilotChatLogic'
import { parseRichText } from '@/lib/richText'
import './CopilotChat.css'

/**
 * Renders a reply with clickable http(s) citations (MASTER.md AA-21).
 * Pure token mapping — no dangerouslySetInnerHTML — so all text stays
 * React-escaped and only vetted links become anchors.
 */
const RichText: React.FC<{ text: string }> = ({ text }) => (
  <>
    {parseRichText(text).map((token, i) => (
      token.type === 'link'
        ? <a key={i} href={token.href} target="_blank" rel="noreferrer noopener" className="copilotChat__inlineLink">{token.text}</a>
        : <React.Fragment key={i}>{token.value}</React.Fragment>
    ))}
  </>
)

const ACTION_LABELS: Record<string, string> = {
  nota: 'Nota',
  compromiso: 'Compromiso',
  persona: 'Persona',
  organizacion: 'Empresa',
  tarea_todo: 'Tarea To Do',
  evento: 'Evento',
  borrador_correo: 'Borrador de correo',
  contacto_outlook: 'Contacto Outlook',
  documento: 'Documento',
}

function actionLabel(entityType: string): string {
  return ACTION_LABELS[entityType] ?? 'Registro'
}

function friendlyNotConfiguredMessage(error: ApiError | Error | null): string {
  const base = 'El servicio de IA no está configurado todavía.'
  if (error instanceof ApiError && error.correlationId) {
    return `${base} Código de referencia: ${error.correlationId}`
  }
  return base
}

/**
 * Copilot Sales (MASTER.md AA-16) chat panel. Single request/response per
 * turn (no streaming — no SSE/WebSocket precedent exists in this codebase;
 * kept as an explicit, reversible v1 simplification). Actions the backend
 * actually performed (creates/updates) are rendered under the assistant's
 * reply so the user always sees what changed, per MASTER's "informar el
 * resultado de cada creación o modificación."
 */
const PENDING_ESTADO_LABEL: Record<string, string> = {
  ejecutada: 'Ejecutada',
  cancelada: 'Cancelada',
  expirada: 'Expirada',
  error: 'Falló',
}

export const CopilotChat: React.FC = () => {
  const { messages, status, error, sendMessage, resolveAction, clearConversation } = useCopilotChat()
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const isSending = status === 'sending'

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!draft.trim() || isSending) return
    const text = draft
    setDraft('')
    void sendMessage(text).then((success) => {
      // Failed send: restore the draft so retrying doesn't require retyping
      // — sendMessage itself already keeps the failed turn out of history,
      // so this retry sends exactly one clean copy, never a duplicate.
      if (!success) setDraft(text)
    })
    requestAnimationFrame(() => inputRef.current?.focus())
  }

  return (
    <div className="copilotChat">
      <div className="copilotChat__intro">
        <span>Pregunta, pide un resumen o cuéntame una reunión, llamada o compromiso para registrarlo.</span>
        {messages.length > 0 && (
          <button type="button" className="copilotChat__clearBtn" onClick={clearConversation}>
            Limpiar conversación
          </button>
        )}
      </div>

      <div className="copilotChat__messages">
        {messages.length === 0 && (
          <div className="copilotChat__empty">
            Ej: "Hoy me reuní con Alfredo en MUT, quedamos en enviarle la propuesta la próxima semana."
          </div>
        )}
        {messages.map((message, index) => (
          <div key={index} className={`copilotChat__message copilotChat__message--${message.role}`}>
            <div className="copilotChat__bubble">
              {message.role === 'assistant' ? <RichText text={message.content} /> : message.content}
            </div>
            {message.pendingActions && message.pendingActions.length > 0 && (
              <div className="copilotChat__pending">
                {message.pendingActions.map((pending) => (
                  <div key={pending.id} className="copilotChat__pendingCard">
                    <div className="copilotChat__pendingResumen">
                      <Badge variant="warning" size="sm" label="Requiere confirmación" />
                      <span>{pending.resumen}</span>
                      {pending.detalles && (pending.detalles.fecha || pending.detalles.hora) && (
                        <span className="copilotChat__pendingDetalle">
                          {[pending.detalles.fecha, pending.detalles.hora].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      {pending.destinatarios && pending.destinatarios.length > 0 && (
                        <span className="copilotChat__pendingDest">Destinatarios: {pending.destinatarios.join(', ')}</span>
                      )}
                    </div>
                    {pending.estado === 'pendiente' ? (
                      <div className="copilotChat__pendingButtons">
                        <Button size="sm" onClick={() => void resolveAction(pending.id, 'confirmar')}>Confirmar</Button>
                        <Button size="sm" variant="secondary" onClick={() => void resolveAction(pending.id, 'cancelar')}>Cancelar</Button>
                        {typeof pending.venceEnSegundos === 'number' && pending.venceEnSegundos > 0 && (
                          <span className="copilotChat__pendingVence">Vence en {Math.ceil(pending.venceEnSegundos / 60)} min</span>
                        )}
                      </div>
                    ) : (
                      <span className="copilotChat__pendingEstado">
                        {PENDING_ESTADO_LABEL[pending.estado ?? 'error'] ?? pending.estado}{pending.resultadoTexto ? ` — ${pending.resultadoTexto}` : ''}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
            {message.executedActions && message.executedActions.length > 0 && (
              <div className="copilotChat__receipts">
                {message.executedActions.map((receipt, receiptIndex) => (
                  <div key={receiptIndex} className={`copilotChat__receipt copilotChat__receipt--${receipt.ok ? 'ok' : 'fail'}`}>
                    {receipt.ok ? (
                      <>
                        <span>✓ {actionLabel(receipt.resourceType ?? '')} verificada — id {receipt.id}</span>
                        {receipt.webUrl && (
                          <a href={receipt.webUrl} target="_blank" rel="noreferrer" className="copilotChat__receiptLink">Abrir</a>
                        )}
                      </>
                    ) : (
                      <span>✗ {actionLabel(receipt.resourceType ?? '')}: resultado no confirmado{receipt.errorCode ? ` (${receipt.errorCode})` : ''} — comprueba la operación antes de repetirla.</span>
                    )}
                  </div>
                ))}
              </div>
            )}
            {message.actions && message.actions.length > 0 && (
              <div className="copilotChat__actions">
                {message.actions.map((action, actionIndex) => {
                  const badge = (
                    <Badge
                      variant="success"
                      size="sm"
                      label={`${actionLabel(action.entityType)} · ${action.summary ?? action.entityId}`}
                    />
                  )
                  return action.link ? (
                    <Link key={actionIndex} to={action.link} className="copilotChat__actionLink">
                      {badge}
                    </Link>
                  ) : (
                    <React.Fragment key={actionIndex}>{badge}</React.Fragment>
                  )
                })}
              </div>
            )}
          </div>
        ))}
        {isSending && <div className="copilotChat__thinking">Copilot está pensando…</div>}
      </div>

      {status === 'not_configured' && (
        <ErrorState title="Copilot no está disponible" message={friendlyNotConfiguredMessage(error)} />
      )}
      {status === 'not_signed_in' && (
        <ErrorState title="Sesión requerida" message="Inicia sesión para usar Copilot Sales." />
      )}
      {status === 'error' && error && (
        <ErrorState title="No se pudo enviar el mensaje" message={friendlyErrorMessage(error)} />
      )}

      <form className="copilotChat__composer" onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          type="text"
          className="copilotChat__input"
          placeholder="Escribe a Copilot Sales…"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          disabled={isSending}
        />
        <Button type="submit" size="sm" disabled={isSending || !draft.trim()}>
          Enviar
        </Button>
      </form>
    </div>
  )
}
